import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import { Box, Card, CardContent, Grid, Pagination, Stack } from '@mui/material';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { ERecordLength } from '../data/Constants';
import { AppDispatch, AppState } from '../store/AppStore';
import { getProductModels } from '../store/states/AppSlice';
import { setProduct } from '../store/states/procos';
import { RefreshButton } from './IconTypes';
import RecordsPerPage from './InputComponents/RecordsPerPage';

/**
 * ProductPortfolio component that displays a paginated grid of product models.
 * Handles product selection, pagination, records per page configuration, and refresh functionality.
 * Integrates with Redux store for state management and React Router for navigation.
 *
 * @returns {JSX.Element} The ProductPortfolio component containing product grid, pagination, and controls
 */
const ProductPortfolio = (): ReactElement => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [productList, setProductList] = useState([]);
  const [catalogPage, setCatalogPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const productModels = useSelector((state: AppState) => state.app.productModels);

  // call dispatch to get the productmodel from the api.
  useEffect(() => {
    if (!productModels.data || productModels.searchKey !== '') {
      dispatch(getProductModels({ page: catalogPage, limit: ERecordLength.RL15 }));
    }
  }, []);

  //Here the SetProductList state is updated which gets ProductModels as props.
  useEffect(() => {
    if (productModels.data) {
      setCatalogPage(productModels.page);
      setProductList(productModels.data[productModels.page]);
      setPageCount(Math.ceil(productModels.totalCount / productModels.limit));
      if (productModels?.searchKey !== '') {
        setPageCount(1);
      }
    } else {
      setProductList([]);
    }
  }, [productModels]);

  /**
   * Handles the change in the number of records to display per page.
   * Resets to the first page and dispatches an action to fetch product models with the new limit.
   *
   * @param {number} limit - The new number of records to display per page
   */
  const handleRecordsChange = (limit: number): void => {
    setCatalogPage(1);
    dispatch(getProductModels({ page: 1, limit: limit }));
  };

  /**
   * Handles pagination changes when the user navigates to a different page.
   * Uses cached data if available, otherwise makes an API call to fetch the new page data.
   *
   * @param {React.ChangeEvent<unknown>} _ - The change event (unused)
   * @param {number} value - The target page number to navigate to
   */
  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number): void => {
    setCatalogPage(value);
    if (productModels.data?.[value]) {
      //The page data already exists
      setProductList(productModels.data[value]);
    } else {
      //Make an api call if no page data found
      dispatch(getProductModels({ page: value, limit: productModels.limit }));
    }
  };

  /**
   * Handles product selection when a user clicks on a product card.
   * Sets the selected product in Redux store and navigates to the Procos page.
   *
   * @param {Object} prod - The selected product object
   * @param {string} prod.code - The product code/ID
   * @param {string} prod.description - The product description/name
   */
  const handleSelect = (prod: { code: string; description: string }): void => {
    dispatch(setProduct({ name: prod.description, id: prod.code }));
    navigate('/procos');
  };

  /**
   * Handles the refresh button click event.
   * Reloads the product models from the first page with the current limit setting.
   */
  const handleRefresh = (): void => {
    dispatch(getProductModels({ page: 1, limit: productModels.limit }));
  };

  /**
   * Renders a "no products found" message based on the current search context.
   * Displays different messages depending on whether a search filter is active.
   *
   * @returns {JSX.Element} A div element containing the appropriate "no data found" message
   */
  const getNoDataFoundLabel = (): ReactElement => {
    return (
      <span className="noData-label">
        {productModels.searchKey ? t('messages.noDataFoundforcriteria') : t('messages.noDataFound')}
      </span>
    );
  };

  return (
    <Box className="list-Section-Tab list-Section-Tab::-webkit-scrollbar">
      <div className="header-action p-3px">
        <RecordsPerPage handleRecordsChange={handleRecordsChange} />
        <RefreshButton handleRefresh={handleRefresh} />
      </div>
      {productList.length === 0 ? (
        getNoDataFoundLabel()
      ) : (
        <Box className="list-Section list-Section::-webkit-scrollbar">
          <Grid container spacing={1} className="list-ItemsContainer">
            {productList.map((prod: { code: string; description: string }, index: number) => {
              return (
                <Grid key={prod.code} className="catalog-Container">
                  <Card sx={{ border: '1px solid grey' }}>
                    <CardContent className="catalog-cardLayout">
                      <Box className="catalog-info">
                        <Box>
                          <span
                            key={index + prod.code + 'productcode'}
                            className="productCatalogCardHeader"
                          >
                            {prod.code}
                          </span>
                        </Box>
                        <Box className="catalog-productDescription">
                          <span
                            key={index + prod.code + 'productdescription'}
                            className="text-capitalize productCatalogCardHeader"
                          >
                            {prod.description}
                          </span>
                        </Box>
                      </Box>
                      <Box className="catalog-icon">
                        <ArrowCircleRightIcon
                          onClick={(): void => handleSelect(prod)}
                          sx={{ fontSize: 50 }}
                          color="primary"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <br />
        </Box>
      )}
      {productList && productList.length > 0 && (
        <Stack spacing={2} className="align-center p-5px">
          <Pagination
            size="small"
            color="primary"
            count={pageCount}
            page={catalogPage}
            onChange={handlePageChange}
          />
        </Stack>
      )}
    </Box>
  );
};

export default ProductPortfolio;
